import type { OrganisationRole } from '@evw/domain';
import { supabase } from '../../lib/supabase';
import type {
  Customer,
  Location,
  Membership,
  Organisation,
  Service,
  StaffMember
} from '../../lib/types';

type MembershipQueryRow = {
  organisation_id: string;
  role: OrganisationRole;
  organisations: Organisation | Organisation[];
};

export async function listMemberships(userId: string): Promise<Membership[]> {
  const { data, error } = await supabase
    .from('organisation_users')
    .select(
      'organisation_id, role, organisations!inner(id,name,slug,status,default_currency,time_zone,deposit_bps,tax_label,tax_registration_number,default_tax_rate,booking_hold_minutes,updated_at)'
    )
    .eq('user_id', userId)
    .eq('status', 'active');

  if (error) throw error;
  return ((data ?? []) as MembershipQueryRow[]).map((row) => ({
    organisation_id: row.organisation_id,
    role: row.role,
    organisation: Array.isArray(row.organisations) ? row.organisations[0]! : row.organisations
  }));
}

export async function updateOrganisation(
  organisationId: string,
  patch: Pick<Organisation, 'name' | 'time_zone' | 'tax_registration_number'>
) {
  const { error } = await supabase.from('organisations').update(patch).eq('id', organisationId);
  if (error) throw error;
}

export async function listLocations(organisationId: string): Promise<Location[]> {
  const { data, error } = await supabase
    .from('locations')
    .select('*')
    .eq('organisation_id', organisationId)
    .is('archived_at', null)
    .order('name');
  if (error) throw error;
  return (data ?? []) as Location[];
}

export async function createLocation(
  organisationId: string,
  input: Pick<Location, 'name' | 'code' | 'time_zone'>
) {
  const { error } = await supabase.from('locations').insert({
    organisation_id: organisationId,
    ...input
  });
  if (error) throw error;
}

export async function updateLocation(
  organisationId: string,
  id: string,
  input: Pick<Location, 'name' | 'code' | 'time_zone'>
) {
  const { error } = await supabase
    .from('locations')
    .update(input)
    .eq('organisation_id', organisationId)
    .eq('id', id);
  if (error) throw error;
}

export async function listStaff(organisationId: string): Promise<StaffMember[]> {
  const { data, error } = await supabase
    .from('staff')
    .select('*')
    .eq('organisation_id', organisationId)
    .is('archived_at', null)
    .order('display_name');
  if (error) throw error;
  return (data ?? []) as StaffMember[];
}

export async function createStaff(
  organisationId: string,
  input: Pick<StaffMember, 'given_name' | 'family_name' | 'display_name' | 'email'>
) {
  const { error } = await supabase.from('staff').insert({
    organisation_id: organisationId,
    ...input,
    email: input.email || null
  });
  if (error) throw error;
}

export async function updateStaff(
  organisationId: string,
  id: string,
  input: Pick<StaffMember, 'given_name' | 'family_name' | 'display_name' | 'email'>
) {
  const { error } = await supabase
    .from('staff')
    .update({ ...input, email: input.email || null })
    .eq('organisation_id', organisationId)
    .eq('id', id);
  if (error) throw error;
}

export async function listServices(organisationId: string): Promise<Service[]> {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('organisation_id', organisationId)
    .is('archived_at', null)
    .order('name');
  if (error) throw error;
  return (data ?? []) as Service[];
}

export async function createService(
  organisationId: string,
  input: Pick<Service, 'name' | 'duration_minutes' | 'gross_amount_minor' | 'currency'>
) {
  const { error } = await supabase.from('services').insert({
    organisation_id: organisationId,
    ...input
  });
  if (error) throw error;
}

export async function updateService(
  organisationId: string,
  id: string,
  input: Pick<Service, 'name' | 'duration_minutes' | 'gross_amount_minor' | 'currency'>
) {
  const { error } = await supabase
    .from('services')
    .update(input)
    .eq('organisation_id', organisationId)
    .eq('id', id);
  if (error) throw error;
}

export async function archiveCoreRecord(
  table: 'locations' | 'staff' | 'services',
  organisationId: string,
  id: string
) {
  const { error } = await supabase
    .from(table)
    .update({ archived_at: new Date().toISOString() })
    .eq('organisation_id', organisationId)
    .eq('id', id);
  if (error) throw error;
}

export async function listCustomers(organisationId: string): Promise<Customer[]> {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('organisation_id', organisationId)
    .is('archived_at', null)
    .order('family_name')
    .limit(200);
  if (error) throw error;
  return (data ?? []) as Customer[];
}

export async function saveCustomer(
  organisationId: string,
  id: string | null,
  input: Pick<Customer, 'given_name' | 'family_name' | 'email' | 'phone'>
) {
  const values = {
    ...input,
    email: input.email || null,
    phone: input.phone || null
  };
  const result = id
    ? await supabase
        .from('customers')
        .update(values)
        .eq('organisation_id', organisationId)
        .eq('id', id)
    : await supabase.from('customers').insert({ organisation_id: organisationId, ...values });
  if (result.error) throw result.error;
}
